import { useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import type { Me } from '@trackflow/shared';
import { api, ApiRequestError, apiPost, setCsrfToken, setUnauthenticatedHandler } from './api';
import { PageLoader } from '../ui/Spinner';

export const ME_KEY = ['auth', 'me'] as const;

async function fetchMe(): Promise<Me | null> {
  try {
    const me = await api<Me>('/auth/me');
    setCsrfToken(me.csrfToken);
    return me;
  } catch (err) {
    if (err instanceof ApiRequestError && err.status === 401) {
      setCsrfToken(undefined);
      return null;
    }
    throw err;
  }
}

/** The signed-in user, or null. Loaded once and shared by every screen. */
export function useMe() {
  return useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 5 * 60_000, retry: false });
}

/** Call after login/register/logout so the whole app sees the new identity. */
export function useSetMe() {
  const qc = useQueryClient();
  return (me: Me | null) => {
    setCsrfToken(me?.csrfToken);
    if (me) qc.setQueryData(ME_KEY, me);
    else {
      qc.clear();
      qc.setQueryData(ME_KEY, null);
    }
  };
}

export function useLogout() {
  const setMe = useSetMe();
  const navigate = useNavigate();
  return async () => {
    await apiPost('/auth/logout').catch(() => undefined);
    setMe(null);
    navigate('/login', { replace: true });
  };
}

/** Wraps merchant screens: signed-out visitors go to /login and come back afterwards. */
export function RequireAuth({ children, admin = false }: { children: ReactNode; admin?: boolean }) {
  const { data: me, isPending } = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      qc.setQueryData(ME_KEY, null);
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}&expired=1`, {
        replace: true,
      });
    });
  }, [qc, navigate, location.pathname, location.search]);

  if (isPending) return <PageLoader />;
  if (!me)
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  if (admin && !me.user.isPlatformAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Wraps login/register: signed-in users skip straight into the app. */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { data: me, isPending } = useMe();
  const [params] = useSearchParams();
  if (isPending) return <PageLoader />;
  if (me) {
    // Honour ?next= (e.g. after an expired session) — this guard re-renders the moment login
    // succeeds, so it decides where the user lands.
    const home = me.merchant ? '/dashboard' : me.user.isPlatformAdmin ? '/admin' : '/settings';
    return <Navigate to={safeNext(params.get('next'), home)} replace />;
  }
  return <>{children}</>;
}

/** Only allow same-site relative redirects (prevents open-redirects via ?next=). */
export function safeNext(next: string | null, fallback = '/dashboard') {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : fallback;
}

/**
 * Embedded mode: the session comes from Shopify (App Bridge ID token). The first request also
 * installs/reconnects the shop on the server. There is no login page to fall back to.
 */
export function RequireShopifySession({ children }: { children: ReactNode }) {
  const { data: me, isPending, isError, refetch, isFetching } = useMe();
  if (isPending) return <PageLoader />;
  if (isError || !me || !me.merchant) {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center px-4 text-center">
        <h1 className="text-xl font-semibold">We couldn't connect to your Shopify store</h1>
        <p className="mt-2 max-w-md text-sm text-ink-600">
          Your Shopify session may have expired, or Shopify is briefly unavailable. Try again, or
          reopen TrackFlow from Apps in your Shopify admin.
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="mt-5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-60"
        >
          {isFetching ? 'Connecting…' : 'Try again'}
        </button>
      </main>
    );
  }
  return <>{children}</>;
}
